import { ClickAwayListener } from "@nova/component-click-away-listener"
import { useObserveElementRect } from "@nova/toolkit-pure-hooks"
import * as Icons from "@nova/component-icons"
import { InputOutlined } from "@nova/ds-component-input"
import {
    ContactList,
    MemberExtended,
    missionGroupColors,
    missionGroupIcons,
    SavedContact,
} from "@nova/schemas"
import {
    formatPartialPhoneNumberInput,
    formatPhoneNumber,
    validateEmailAddress,
} from "@nova/util-strings"
import classNames from "classnames"
import { CreateSavedContactModal } from "features/data-management/workspace/workspace/body/settings-and-collaborators/mission-groups/SavedContactsModals"
import {
    useCreateSavedContact,
    useSavedContacts,
} from "features/data-management/workspace/workspace/body/settings-and-collaborators/mission-groups/SavedContactsQueries"
import { useContactLists } from "features/data-management/workspace/workspace/body/settings-and-collaborators/mission-groups/useContactListFunctions"
import { MissionGroupIcon } from "features/data-management/workspace/workspace/body/settings-and-collaborators/mission-groups/MissionGroupIcon"
import { WorkspaceCollaboratorProfile } from "features/data-management/workspace/workspace/body/settings-and-collaborators/collaborators/WorkspaceCollaboratorProfile"
import { selectWorkspaceMembers } from "features/entity-caching/members/UserManagementSlice"
import { useAppSelector } from "integrations/redux/hooks"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useConfirmationModalContext } from "features/modals/WorkspaceConfirmationModalV2"
import { ContactChipBox } from "./ContactChipBox"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"

type EnabledFeature = "email" | "phone" | "missionGroups"

type DropdownOption =
    | { kind: "member"; member: MemberExtended }
    | { kind: "savedContactEmail"; contact: SavedContact }
    | { kind: "savedContactPhone"; contact: SavedContact }
    | { kind: "missionGroup"; group: ContactList }
    | { kind: "rawEmail"; value: string }
    | { kind: "rawPhone"; value: string }

type FeatureProps<T extends readonly EnabledFeature[]> = ("email" extends T[number]
    ? { emails: string[]; setEmails: (v: string[]) => void }
    : { emails?: never; setEmails?: never }) &
    ("phone" extends T[number]
        ? { phoneNumbers: string[]; setPhoneNumbers: (v: string[]) => void }
        : { phoneNumbers?: never; setPhoneNumbers?: never }) &
    ("missionGroups" extends T[number]
        ? { missionGroups: ContactList[]; setMissionGroups: (v: ContactList[]) => void }
        : { missionGroups?: never; setMissionGroups?: never })

function useContactInputData(enabledFeatures: readonly EnabledFeature[]) {
    const members = useAppSelector(selectWorkspaceMembers)
    const { contacts: savedContacts } = useSavedContacts()

    const hasMissionGroups = enabledFeatures.includes("missionGroups")
    const { contactLists } = useContactLists()

    const filteredSavedContacts = useMemo(() => {
        if (enabledFeatures.includes("email") && enabledFeatures.includes("phone")) {
            return savedContacts
        }
        if (enabledFeatures.includes("email")) {
            return savedContacts
                .filter((contact) => !!contact.email)
                .map((contact) => ({ ...contact, phone: undefined }))
        }
        if (enabledFeatures.includes("phone")) {
            return savedContacts
                .filter((contact) => !!contact.phone)
                .map((contact) => ({ ...contact, email: undefined }))
        }
        return []
    }, [enabledFeatures, savedContacts])

    return {
        members: enabledFeatures.includes("email") ? (members ?? []) : [],
        savedContacts: filteredSavedContacts,
        allMissionGroups: hasMissionGroups ? contactLists : [],
    }
}

function computeOptions(
    textInput: string,
    enabledFeatures: readonly EnabledFeature[],
    members: MemberExtended[],
    savedContacts: SavedContact[],
    allMissionGroups: ContactList[],
    selectedEmails: string[],
    selectedPhones: string[],
    selectedMissionGroups: ContactList[]
): DropdownOption[] {
    const query = textInput.toLowerCase().trim()
    const options: DropdownOption[] = []

    const selectedGroupIds = new Set(selectedMissionGroups.map((g) => g.id))

    for (const group of allMissionGroups) {
        if (selectedGroupIds.has(group.id)) continue
        if (!query || group.name.toLowerCase().includes(query)) {
            options.push({ kind: "missionGroup", group })
        }
    }

    for (const member of members) {
        if (!query) {
            options.push({ kind: "member", member })
        } else {
            const searchStr = `${member.fullName ?? ""} ${member.email ?? ""}`.toLowerCase()
            if (searchStr.includes(query)) {
                options.push({ kind: "member", member })
            }
        }
    }

    for (const contact of savedContacts) {
        const nameMatch = !query || contact.name.toLowerCase().includes(query)
        const emailMatch = !!query && !!contact.email && contact.email.toLowerCase().includes(query)
        const phoneMatch =
            !!query &&
            !!contact.phone &&
            (contact.phone.toLowerCase().includes(query) ||
                contact.phone.includes(formatPhoneNumber(textInput) ?? "____"))

        if (nameMatch) {
            if (contact.email && !selectedEmails.includes(contact.email)) {
                options.push({ kind: "savedContactEmail", contact })
            }
            if (contact.phone && !selectedPhones.includes(contact.phone)) {
                options.push({ kind: "savedContactPhone", contact })
            }
        } else if (emailMatch && contact.email && !selectedEmails.includes(contact.email)) {
            options.push({ kind: "savedContactEmail", contact })
        } else if (phoneMatch && contact.phone && !selectedPhones.includes(contact.phone)) {
            options.push({ kind: "savedContactPhone", contact })
        }
    }

    if (query) {
        const rawPhone = enabledFeatures.includes("phone") ? formatPhoneNumber(textInput) : null
        if (
            rawPhone &&
            !selectedPhones.includes(rawPhone) &&
            !options.some(
                (option) => option.kind === "savedContactPhone" && option.contact.phone === rawPhone
            )
        ) {
            options.push({ kind: "rawPhone", value: rawPhone })
        }

        if (
            enabledFeatures.includes("email") &&
            validateEmailAddress(textInput) &&
            !selectedEmails.includes(textInput) &&
            !options.some(
                (option) =>
                    (option.kind === "member" && option.member.email === textInput) ||
                    (option.kind === "savedContactEmail" && option.contact.email === textInput)
            )
        ) {
            options.push({ kind: "rawEmail", value: textInput })
        }
    }

    return options
}

const DropdownRow = ({
    option,
    highlighted,
    selectedEmails,
}: {
    option: DropdownOption
    highlighted: boolean
    selectedEmails: string[]
}) => {
    return (
        <div
            className={classNames("flex items-center gap-3 px-4 py-2 cursor-pointer", {
                "bg-gray-600": highlighted,
                "hover:bg-gray-700": !highlighted,
            })}
        >
            {option.kind === "member" && (
                <WorkspaceCollaboratorProfile
                    userId={option.member.userId}
                    email={option.member.email}
                    fullName={option.member.fullName}
                    isFullWidth
                    isAlreadyAdded={
                        (!!option.member.email && selectedEmails.includes(option.member.email)) ||
                        selectedEmails.includes(option.member.userId)
                    }
                />
            )}
            {option.kind === "savedContactEmail" && (
                <>
                    <div className="w-8 h-8 shrink-0 flex justify-center items-center">
                        <Icons.SmallIcon Icon={Icons.Email} className="text-gray-200" />
                    </div>
                    <div className="shrink min-w-0 truncate">
                        <h5 className="text-gray-0">{option.contact.name}</h5>
                        <small className="text-gray-200">{option.contact.email}</small>
                    </div>
                </>
            )}
            {option.kind === "savedContactPhone" && (
                <>
                    <div className="w-8 h-8 shrink-0 flex justify-center items-center">
                        <Icons.SmallIcon Icon={Icons.PhoneMessage} className="text-gray-200" />
                    </div>
                    <div className="shrink min-w-0 truncate">
                        <h5 className="text-gray-0">{option.contact.name}</h5>
                        <small className="text-gray-200">{option.contact.phone}</small>
                    </div>
                </>
            )}
            {option.kind === "missionGroup" && (
                <>
                    <MissionGroupIcon
                        icon={option.group.icon ?? missionGroupIcons[0]}
                        color={option.group.color ?? missionGroupColors[0]}
                        size="24"
                    />
                    <h5 className="text-gray-0 shrink min-w-0 truncate">{option.group.name}</h5>
                </>
            )}
            {option.kind === "rawEmail" && (
                <>
                    <div className="w-8 h-8 shrink-0 flex justify-center items-center">
                        <Icons.SmallIcon Icon={Icons.Email} className="text-gray-200" />
                    </div>
                    <h5 className="text-gray-0 shrink min-w-0 truncate">{option.value}</h5>
                </>
            )}
            {option.kind === "rawPhone" && (
                <>
                    <div className="w-8 h-8 shrink-0 flex justify-center items-center">
                        <Icons.SmallIcon Icon={Icons.PhoneMessage} className="text-gray-200" />
                    </div>
                    <h5 className="text-gray-0 shrink min-w-0 truncate">{option.value}</h5>
                </>
            )}
        </div>
    )
}

const ContactInputDropdown = ({
    options,
    highlightedIndex,
    onSelect,
    onHighlight,
    anchorElement,
    selectedEmails,
}: {
    options: DropdownOption[]
    highlightedIndex: number
    onSelect: (option: DropdownOption) => void
    onHighlight: (index: number) => void
    anchorElement: HTMLElement | null
    selectedEmails: string[]
}) => {
    const itemRefs = useRef<(HTMLDivElement | null)[]>([])
    const { top, left, width, height } = useObserveElementRect({
        element: anchorElement,
        observePosition: true,
    })

    useEffect(() => {
        itemRefs.current[highlightedIndex]?.scrollIntoView({ block: "nearest" })
    }, [highlightedIndex])

    return createPortal(
        <div
            className="fixed z-[9999] rounded-lg bg-gray-800 border border-gray-600 shadow-lg overflow-hidden"
            style={{ top: top + height + 4, left, width }}
        >
            <ScrollableArea style={{ maxHeight: 200 }}>
                {options.map((option, i) => (
                    <div
                        key={i}
                        ref={(el) => {
                            itemRefs.current[i] = el
                        }}
                        onMouseDown={(e) => {
                            e.preventDefault()
                            onSelect(option)
                        }}
                        onMouseEnter={() => onHighlight(i)}
                    >
                        <DropdownRow
                            option={option}
                            highlighted={i === highlightedIndex}
                            selectedEmails={selectedEmails}
                        />
                    </div>
                ))}
            </ScrollableArea>
        </div>,
        document.getElementById("portal")!
    )
}

function useContactInputCore({
    enabledFeatures,
    promptToSaveContacts,
    selectedEmails,
    selectedPhones,
    selectedMissionGroups,
    onAddEmail,
    onAddPhone,
    onAddMissionGroup,
}: {
    enabledFeatures: readonly EnabledFeature[]
    promptToSaveContacts: boolean
    selectedEmails: string[]
    selectedPhones: string[]
    selectedMissionGroups: ContactList[]
    onAddEmail: (email: string) => void
    onAddPhone: (phone: string) => void
    onAddMissionGroup: (group: ContactList) => void
}) {
    const { members, savedContacts, allMissionGroups } = useContactInputData(enabledFeatures)
    const { createSavedContact } = useCreateSavedContact()

    const [textInput, setTextInput] = useState("")
    const [menuOpen, setMenuOpen] = useState(false)
    const [highlightedIndex, setHighlightedIndex] = useState(0)

    const confirmationModal = useConfirmationModalContext()
    const { isEditor } = useWorkspacePermissions()

    const options = computeOptions(
        textInput,
        enabledFeatures,
        members,
        savedContacts,
        allMissionGroups,
        selectedEmails,
        selectedPhones,
        selectedMissionGroups
    )

    const handleInputChange = useCallback(
        (value: string) => {
            const formatted = enabledFeatures.includes("phone")
                ? formatPartialPhoneNumberInput(value)
                : value
            setTextInput(formatted)
            setMenuOpen(true)
            setHighlightedIndex(0)
        },
        [enabledFeatures]
    )

    const handleSelect = useCallback(
        (option: DropdownOption) => {
            setMenuOpen(false)
            setTextInput("")
            setHighlightedIndex(0)

            if (
                option.kind === "missionGroup" &&
                !selectedMissionGroups.some((group) => group.id === option.group.id)
            ) {
                onAddMissionGroup(option.group)
                return
            }
            if (option.kind === "member" && !selectedEmails.includes(option.member.email)) {
                onAddEmail(option.member.email || option.member.userId)
                return
            }
            if (
                option.kind === "savedContactEmail" &&
                !selectedEmails.includes(option.contact.email ?? "")
            ) {
                onAddEmail(option.contact.email!)
                return
            }
            if (
                option.kind === "savedContactPhone" &&
                !selectedPhones.includes(option.contact.phone ?? "")
            ) {
                onAddPhone(option.contact.phone!)
                return
            }
            if (option.kind === "rawEmail" && !selectedEmails.includes(option.value)) {
                onAddEmail(option.value)
                if (promptToSaveContacts && isEditor) {
                    confirmationModal.confirm<Pick<SavedContact, "name" | "email" | "phone">>({
                        RenderComponent: ({
                            confirm,
                            cancel,
                            isPerformingAsyncConfirmCallback,
                        }) => (
                            <CreateSavedContactModal
                                confirm={confirm}
                                cancel={cancel}
                                cancelText="Skip"
                                isPerformingAsyncConfirmCallback={isPerformingAsyncConfirmCallback}
                                initialEmail={option.value}
                            />
                        ),
                        asyncConfirmCallback: async ({ name, email, phone }) => {
                            await createSavedContact({ name, email, phone })
                        },
                    })
                }
                return
            }
            if (option.kind === "rawPhone" && !selectedPhones.includes(option.value)) {
                onAddPhone(option.value)
                if (promptToSaveContacts && isEditor) {
                    confirmationModal.confirm<Pick<SavedContact, "name" | "email" | "phone">>({
                        RenderComponent: ({
                            confirm,
                            cancel,
                            isPerformingAsyncConfirmCallback,
                        }) => (
                            <CreateSavedContactModal
                                confirm={confirm}
                                cancel={cancel}
                                cancelText="Skip"
                                isPerformingAsyncConfirmCallback={isPerformingAsyncConfirmCallback}
                                initialPhone={option.value}
                            />
                        ),
                        asyncConfirmCallback: async ({ name, email, phone }) => {
                            await createSavedContact({ name, email, phone })
                        },
                    })
                }
            }
        },
        [
            selectedMissionGroups,
            selectedEmails,
            selectedPhones,
            onAddMissionGroup,
            onAddEmail,
            onAddPhone,
            promptToSaveContacts,
            isEditor,
            confirmationModal,
            createSavedContact,
        ]
    )

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (!menuOpen) {
                if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                    setMenuOpen(true)
                }
                return
            }
            if (e.key === "ArrowDown") {
                e.preventDefault()
                setHighlightedIndex((i) => Math.min(i + 1, options.length - 1))
            } else if (e.key === "ArrowUp") {
                e.preventDefault()
                setHighlightedIndex((i) => Math.max(i - 1, 0))
            } else if (e.key === "Enter" && options[highlightedIndex]) {
                e.preventDefault()
                handleSelect(options[highlightedIndex])
            } else if (e.key === "Escape") {
                setMenuOpen(false)
            }
        },
        [menuOpen, options, highlightedIndex, handleSelect]
    )

    return {
        textInput,
        setTextInput,
        menuOpen,
        setMenuOpen,
        highlightedIndex,
        setHighlightedIndex,
        options,
        handleInputChange,
        handleSelect,
        handleKeyDown,
    }
}

type MultiSelectBaseProps = {
    enabledFeatures: readonly EnabledFeature[]
    promptToSaveContacts?: boolean
    placeholder?: string
    warnForPhoneNumbers?: boolean
    includePlusIcon?: boolean
}

export const ContactInputMultiSelect = <T extends readonly EnabledFeature[]>({
    enabledFeatures,
    promptToSaveContacts = false,
    placeholder = "Add phone numbers for SMS, emails, or lists",
    warnForPhoneNumbers,
    includePlusIcon = true,
    emails,
    setEmails,
    phoneNumbers,
    setPhoneNumbers,
    missionGroups,
    setMissionGroups,
}: MultiSelectBaseProps & { enabledFeatures: T } & FeatureProps<T>) => {
    const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null)

    const safeEmails = useMemo(() => (emails as string[] | undefined) ?? [], [emails])
    const safePhones = useMemo(() => (phoneNumbers as string[] | undefined) ?? [], [phoneNumbers])
    const safeMissionGroups = useMemo(
        () => (missionGroups as ContactList[] | undefined) ?? [],
        [missionGroups]
    )

    const onAddEmail = useCallback(
        (email: string) =>
            (setEmails as ((v: string[]) => void) | undefined)?.([...safeEmails, email]),
        [setEmails, safeEmails]
    )
    const onAddPhone = useCallback(
        (phone: string) =>
            (setPhoneNumbers as ((v: string[]) => void) | undefined)?.([...safePhones, phone]),
        [setPhoneNumbers, safePhones]
    )
    const onAddMissionGroup = useCallback(
        (group: ContactList) =>
            (setMissionGroups as ((v: ContactList[]) => void) | undefined)?.([
                ...safeMissionGroups,
                group,
            ]),
        [setMissionGroups, safeMissionGroups]
    )

    const {
        textInput,
        menuOpen,
        setMenuOpen,
        highlightedIndex,
        setHighlightedIndex,
        options,
        handleInputChange,
        handleSelect,
        handleKeyDown,
    } = useContactInputCore({
        enabledFeatures,
        promptToSaveContacts,
        selectedEmails: safeEmails,
        selectedPhones: safePhones,
        selectedMissionGroups: safeMissionGroups,
        onAddEmail,
        onAddPhone,
        onAddMissionGroup,
    })

    return (
        <ClickAwayListener onClickAway={() => setMenuOpen(false)} className="relative">
            <div ref={setAnchorElement} onKeyDown={handleKeyDown}>
                <ContactChipBox
                    textInput={textInput}
                    setTextInput={handleInputChange}
                    emails={safeEmails}
                    setEmails={(v) => setEmails?.(v)}
                    phoneNumbers={safePhones}
                    setPhoneNumbers={(v) => setPhoneNumbers?.(v)}
                    missionGroups={safeMissionGroups}
                    setMissionGroups={(v) => setMissionGroups?.(v)}
                    warnForPhoneNumbers={warnForPhoneNumbers}
                    placeholder={placeholder}
                    includePlusIcon={includePlusIcon}
                />
            </div>
            {menuOpen && textInput.trim().length > 0 && options.length > 0 && (
                <ContactInputDropdown
                    options={options}
                    highlightedIndex={highlightedIndex}
                    onSelect={handleSelect}
                    onHighlight={setHighlightedIndex}
                    anchorElement={anchorElement}
                    selectedEmails={safeEmails}
                />
            )}
        </ClickAwayListener>
    )
}

type SingleSelectBaseProps = {
    enabledFeatures: readonly EnabledFeature[]
    onSelect: (option: DropdownOption) => void
    promptToSaveContacts?: boolean
    placeholder?: string
    disabled?: boolean
    leftItem?: React.ReactNode
}

export const ContactInputSingleSelect = <T extends readonly EnabledFeature[]>({
    enabledFeatures,
    onSelect,
    promptToSaveContacts = false,
    placeholder = "Add names, emails, or phone numbers",
    disabled,
    leftItem,
}: SingleSelectBaseProps & { enabledFeatures: T }) => {
    const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    const handleAddEmail = useCallback(
        (email: string) => onSelect({ kind: "rawEmail", value: email }),
        [onSelect]
    )
    const handleAddPhone = useCallback(
        (phone: string) => onSelect({ kind: "rawPhone", value: phone }),
        [onSelect]
    )
    const handleAddMissionGroup = useCallback(
        (group: ContactList) => onSelect({ kind: "missionGroup", group }),
        [onSelect]
    )

    const {
        textInput,
        setTextInput,
        menuOpen,
        setMenuOpen,
        highlightedIndex,
        setHighlightedIndex,
        options,
        handleInputChange,
        handleKeyDown,
    } = useContactInputCore({
        enabledFeatures,
        promptToSaveContacts,
        selectedEmails: [],
        selectedPhones: [],
        selectedMissionGroups: [],
        onAddEmail: handleAddEmail,
        onAddPhone: handleAddPhone,
        onAddMissionGroup: handleAddMissionGroup,
    })

    const confirmationModal = useConfirmationModalContext()
    const { createSavedContact } = useCreateSavedContact()
    const { isEditor } = useWorkspacePermissions()

    const handleSelectOption = useCallback(
        async (option: DropdownOption) => {
            onSelect(option)
            if (
                isEditor &&
                (option.kind === "rawEmail" || option.kind === "rawPhone") &&
                promptToSaveContacts
            ) {
                await confirmationModal.confirm<Pick<SavedContact, "name" | "email" | "phone">>({
                    RenderComponent: ({ confirm, cancel, isPerformingAsyncConfirmCallback }) => (
                        <CreateSavedContactModal
                            confirm={confirm}
                            cancel={cancel}
                            isPerformingAsyncConfirmCallback={isPerformingAsyncConfirmCallback}
                            cancelText="Skip"
                            initialEmail={option.kind === "rawEmail" ? option.value : undefined}
                            initialPhone={option.kind === "rawPhone" ? option.value : undefined}
                        />
                    ),
                    asyncConfirmCallback: async ({ name, email, phone }) => {
                        await createSavedContact({ name, email, phone })
                    },
                })
            }
            setTextInput("")
            setMenuOpen(false)
            inputRef.current?.focus()
        },
        [
            onSelect,
            isEditor,
            promptToSaveContacts,
            setTextInput,
            setMenuOpen,
            confirmationModal,
            createSavedContact,
        ]
    )

    return (
        <ClickAwayListener onClickAway={() => setMenuOpen(false)} className="relative">
            <div ref={setAnchorElement} onKeyDown={handleKeyDown}>
                <InputOutlined
                    ref={inputRef}
                    value={textInput}
                    onChange={(e) => handleInputChange(e.target.value)}
                    placeholder={placeholder}
                    disabled={disabled}
                    leftItem={leftItem}
                    onFocus={() => setMenuOpen(true)}
                />
            </div>
            {menuOpen && options.length > 0 && (
                <ContactInputDropdown
                    options={options}
                    highlightedIndex={highlightedIndex}
                    onSelect={handleSelectOption}
                    onHighlight={setHighlightedIndex}
                    anchorElement={anchorElement}
                    selectedEmails={[]}
                />
            )}
        </ClickAwayListener>
    )
}
